import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import VideoRowCard from "@/components/feature/VideoRowCard";
import Pagination from "@/components/base/Pagination";
import FilterPanel, { type FilterState } from "@/pages/category/components/FilterPanel";
import { allVideos, getCategoryMeta, matchHeat, sortOptions, sortVideos } from "@/lib/videoData";
import { usePageMeta } from "@/hooks/usePageMeta";

const PAGE_SIZE = 6;

const initialFilters: FilterState = { region: "全部", year: "全部", heat: "全部", sort: "latest" };

export default function CategoryPage() {
  const { type } = useParams();
  const meta = getCategoryMeta(type ?? "");
  const [filters, setFilters] = useState<FilterState>(initialFilters);
  const [page, setPage] = useState(1);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const isHydrated = useRef(false);

  usePageMeta(
    meta ? `${meta.name} - 糖豆视频 精选影视分类` : "分类未找到 - 糖豆视频",
    meta
      ? `${meta.description} 支持按地区、年份与热度筛选，并提供最新更新、最多热度与评分最高三种排序方式。`
      : "该分类暂不存在，请返回首页浏览糖豆视频精选影视综艺内容。",
    meta ? `${meta.name},${meta.name}推荐,糖豆视频,高清影视,在线观看` : "糖豆视频,影视分类",
  );

  const filtered = useMemo(() => {
    if (!meta) return [];
    const base = allVideos.filter(
      (video) =>
        video.category === meta.slug &&
        (filters.region === "全部" || video.region === filters.region) &&
        (filters.year === "全部" || String(video.year) === filters.year) &&
        matchHeat(video.heat, filters.heat),
    );
    return sortVideos(base, filters.sort);
  }, [meta, filters]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [filters]);

  useEffect(() => {
    if (!isHydrated.current) {
      isHydrated.current = true;
      return;
    }
    listRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [safePage]);

  useEffect(() => {
    document.body.style.overflow = mobileFilterOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileFilterOpen]);

  const updateFilters = (patch: Partial<FilterState>) => {
    setFilters((prev) => ({ ...prev, ...patch }));
  };

  const resetFilters = () => {
    setFilters(initialFilters);
  };

  const activeCount = [filters.region, filters.year, filters.heat].filter((item) => item !== "全部").length;

  if (!meta) {
    return (
      <div className="flex min-h-screen w-full flex-col bg-background-100">
        <Navbar />
        <main className="mx-auto flex w-full max-w-[1360px] flex-1 flex-col items-center justify-center px-4 py-24 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-50 text-primary-600">
            <i className="ri-film-line text-3xl" />
          </span>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">未找到该分类</h1>
          <p className="mt-2 text-sm text-foreground-600">糖豆视频目前提供电影、电视剧与综艺三大分类。</p>
          <div className="mt-6 flex flex-wrap justify-center gap-2">
            {["movie", "tv", "variety"].map((slug) => (
              <Link
                key={slug}
                to={`/category/${slug}`}
                className="flex h-10 items-center whitespace-nowrap rounded-md bg-primary-800 px-4 text-xs font-semibold text-background-50"
              >
                {getCategoryMeta(slug)?.name}
              </Link>
            ))}
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-100">
      <Navbar />
      <main className="mx-auto w-full max-w-[1360px] flex-1 px-4 pb-4 pt-6 md:px-8">
        <nav className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500" aria-label="面包屑导航">
          <Link to="/" className="transition-colors hover:text-primary-700">
            首页
          </Link>
          <i className="ri-arrow-right-s-line text-sm text-foreground-400" />
          <span className="font-medium text-foreground-800">{meta.name}</span>
        </nav>

        <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="text-[10px] font-bold tracking-[0.26em] text-accent-700">{meta.en}</span>
            <h1 className="mt-1.5 font-heading text-3xl font-extrabold tracking-tight text-foreground-950 md:text-[40px]">
              {meta.name}
            </h1>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">{meta.description}</p>
          </div>
          <div className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 px-4 py-3">
            <div>
              <p className="font-heading text-lg font-extrabold text-primary-800">
                {filtered.length}
                <span className="ml-1 text-xs font-medium text-foreground-500">部</span>
              </p>
              <p className="text-[10px] text-foreground-500">符合当前筛选</p>
            </div>
            <span className="h-8 w-px bg-background-200" />
            <div>
              <p className="font-heading text-lg font-extrabold text-foreground-900">{safePage}/{totalPages}</p>
              <p className="text-[10px] text-foreground-500">当前页</p>
            </div>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-[236px_1fr] lg:gap-7">
          <aside className="hidden self-start rounded-lg border border-background-200 bg-background-50 p-4 lg:sticky lg:top-24 lg:block">
            <div className="mb-4 flex items-center justify-between">
              <p className="flex items-center gap-1.5 text-sm font-semibold text-foreground-950">
                <i className="ri-filter-3-line text-base text-primary-600" />
                筛选条件
              </p>
              {activeCount > 0 && (
                <span className="rounded bg-accent-100 px-1.5 py-0.5 text-[10px] font-semibold text-accent-800">
                  {activeCount} 项
                </span>
              )}
            </div>
            <FilterPanel
              filters={filters}
              onChange={updateFilters}
              onReset={resetFilters}
              resultCount={filtered.length}
              showSort
            />
          </aside>

          <div ref={listRef}>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setMobileFilterOpen(true)}
                  className="flex h-9 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 text-xs font-medium text-foreground-800 lg:hidden"
                >
                  <i className="ri-filter-3-line text-sm text-primary-600" />
                  筛选
                  {activeCount > 0 && (
                    <span className="flex h-4 w-4 items-center justify-center rounded-full bg-accent-500 text-[9px] font-bold text-foreground-950">
                      {activeCount}
                    </span>
                  )}
                </button>
                <p className="text-xs text-foreground-500">
                  共 <span className="font-semibold text-foreground-900">{filtered.length}</span> 部内容
                </p>
              </div>

              <div className="no-scrollbar -mx-1 flex max-w-full items-center gap-1 overflow-x-auto px-1 py-1">
                {sortOptions.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => updateFilters({ sort: option.key })}
                    className={`flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-full px-3 py-1.5 text-[11px] font-medium transition-colors ${
                      filters.sort === option.key
                        ? "bg-primary-800 text-background-50"
                        : "bg-background-50 text-foreground-600 hover:text-primary-700"
                    }`}
                  >
                    <i className={`${option.icon} text-xs`} />
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {pageItems.length > 0 ? (
              <>
                <div className="space-y-3">
                  {pageItems.map((video) => (
                    <VideoRowCard key={video.id} video={video} />
                  ))}
                </div>
                <Pagination page={safePage} totalPages={totalPages} onChange={setPage} />
              </>
            ) : (
              <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                  <i className="ri-search-eye-line text-2xl" />
                </span>
                <p className="mt-4 font-heading text-base font-semibold text-foreground-900">抱歉，未找到相关视频</p>
                <p className="mt-1.5 max-w-sm text-xs leading-relaxed text-foreground-500">
                  当前筛选组合下{meta.name}分类暂无内容，试试放宽年份或热门程度条件。
                </p>
                <button
                  type="button"
                  onClick={resetFilters}
                  className="mt-5 flex h-9 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-800 px-4 text-xs font-semibold text-background-50 transition-colors hover:bg-primary-900"
                >
                  <i className="ri-refresh-line text-sm" />
                  重置筛选
                </button>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />

      {mobileFilterOpen && (
        <div className="fixed inset-0 z-[70] flex flex-col bg-background-50 lg:hidden">
          <div className="flex items-center justify-between border-b border-background-200 px-4 py-3">
            <p className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
              <i className="ri-filter-3-line text-primary-600" />
              筛选{meta.name}
            </p>
            <button
              type="button"
              aria-label="关闭筛选"
              onClick={() => setMobileFilterOpen(false)}
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-700"
            >
              <i className="ri-close-line text-lg" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-5">
            <FilterPanel
              filters={filters}
              onChange={updateFilters}
              onReset={resetFilters}
              resultCount={filtered.length}
              showSort
            />
          </div>
          <div className="flex gap-2 border-t border-background-200 px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
            <button
              type="button"
              onClick={resetFilters}
              className="h-11 flex-1 cursor-pointer rounded-md border border-background-300 text-sm font-medium text-foreground-700"
            >
              重置
            </button>
            <button
              type="button"
              onClick={() => setMobileFilterOpen(false)}
              className="h-11 flex-[2] cursor-pointer rounded-md bg-primary-800 text-sm font-semibold text-background-50"
            >
              查看 {filtered.length} 部结果
            </button>
          </div>
        </div>
      )}
    </div>
  );
}